import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserCircle2, KeyRound, Users, LogOut } from "lucide-react";

export default function AccountCard() {
  const { user, status, logout, refreshStatus } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [signups, setSignups] = useState(!!status?.allow_signups);
  const isOwner = user?.role === "owner";

  const changePassword = async () => {
    try {
      await api.changePassword(pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    }
  };

  const toggleSignups = async (v) => {
    try {
      await api.setAuthSettings({ allow_signups: v });
      setSignups(v);
      refreshStatus?.();
      toast.success(v ? "Public sign-ups are now OPEN" : "Sign-ups closed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-5" data-testid="account-card">
      <div className="flex items-center gap-2 flex-wrap">
        <UserCircle2 className="h-4 w-4 text-cyan-400" />
        <h3 className="font-display font-bold text-base">Account</h3>
        <span data-testid="account-role-badge"
          className="px-2 py-0.5 rounded font-mono text-[10px] uppercase border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
          {user?.role}
        </span>
        <span className="font-mono text-xs text-slate-400 ml-auto">{user?.email}</span>
        <Button size="sm" variant="ghost" data-testid="account-logout-button" onClick={logout}
          className="h-7 text-slate-400 hover:text-rose-300">
          <LogOut className="h-3.5 w-3.5 mr-1" /><span className="font-mono text-[11px]">Sign out</span>
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="text-[11px] text-slate-400 flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Current password</Label>
          <Input data-testid="account-current-password" type="password" value={pw.current_password}
            onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))}
            className="bg-[#0E1420] border-border font-mono text-sm" autoComplete="current-password" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] text-slate-400">New password</Label>
          <Input data-testid="account-new-password" type="password" value={pw.new_password}
            onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))}
            placeholder="at least 8 characters"
            className="bg-[#0E1420] border-border font-mono text-sm" autoComplete="new-password" />
        </div>
        <Button data-testid="account-change-password-button" onClick={changePassword}
          disabled={!pw.current_password || pw.new_password.length < 8}
          className="bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold disabled:opacity-40">
          Change password
        </Button>
      </div>

      {isOwner && (
        <div className="flex items-center justify-between rounded-md border border-border bg-[#0E1420] px-4 py-3"
          data-testid="signups-toggle-row">
          <div>
            <Label className="text-sm text-slate-300 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Public sign-ups</Label>
            <p className="text-[11px] text-slate-600 font-mono mt-0.5">
              Off = only you. On = anyone can register; each account only sees its own crawls, datasets and files.
            </p>
          </div>
          <Switch data-testid="signups-enabled-switch" checked={signups} onCheckedChange={toggleSignups} />
        </div>
      )}
    </div>
  );
}
